import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

import type { BorderColor } from '../border';
import { hexColorSwatchValue } from '../hex-color';
import { HexColorError, useHexColorInput } from './use-hex-color-input';
import {
  FIELD_CONTROL_FONT_SIZE,
  FIELD_CONTROL_PADDING,
  FIELD_CONTROL_RADIUS,
  fieldControlStyle,
  selectControlStyle,
  disabledSelectControlStyle,
} from './field-control-style';

type BorderColorSelectFieldProps = {
  field: { label?: string };
  name: string;
  value: BorderColor;
  onChange: (value: BorderColor) => void;
  readOnly?: boolean;
};

const ROLE_OPTIONS: readonly { label: string; value: 'none' | 'theme' }[] = [
  { label: 'None', value: 'none' },
  { label: 'Theme', value: 'theme' },
];

const CUSTOM_OPTION_VALUE = 'custom';

/** Starting hex when the author switches from a role to Custom. */
const CUSTOM_DEFAULT = '#c8d2db';

const dropdownStyle: React.CSSProperties = {
  ...selectControlStyle,
  padding: `var(--dcm-cms-border-color-select-padding, ${FIELD_CONTROL_PADDING})`,
  paddingRight: selectControlStyle.paddingRight,
  borderColor:
    'var(--dcm-cms-border-color-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)))',
  borderRadius: `var(--dcm-cms-border-color-border-radius, ${FIELD_CONTROL_RADIUS})`,
  fontSize: `var(--dcm-cms-border-color-font-size, ${FIELD_CONTROL_FONT_SIZE})`,
  color: 'var(--dcm-cms-border-color-text-color, var(--puck-field-color-text, var(--puck-color-text, #121516)))',
  backgroundColor: 'var(--dcm-cms-border-color-bg, var(--puck-field-color-bg, #fff))',
};

/**
 * Puck custom field for `BorderColor`: None / Theme, plus a custom hex escape hatch. The value is
 * a discriminated union -- `{ style: 'none' | 'theme' }` or `{ style: 'custom', hex }` -- rather
 * than an ambiguous string, so there is no role-vs-hex sniffing to get wrong; `value.style` says
 * outright which case this is.
 */
export function BorderColorSelectField({
  field,
  name,
  value,
  onChange,
  readOnly,
}: BorderColorSelectFieldProps): JSX.Element {
  const selectValue = value.style;

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const chosen = e.currentTarget.value;
      if (chosen === CUSTOM_OPTION_VALUE) {
        // Only seed the starting hex when not already in custom mode.
        if (value.style !== 'custom') onChange({ style: 'custom', hex: CUSTOM_DEFAULT });
      } else {
        onChange({ style: chosen as 'none' | 'theme' });
      }
    },
    [value, onChange]
  );

  const labelText = field.label ?? name;
  const hex = value.style === 'custom' ? value.hex : CUSTOM_DEFAULT;
  const handleColorChange = useCallback((color: string) => onChange({ style: 'custom', hex: color }), [onChange]);
  const hexInput = useHexColorInput(hex, handleColorChange);

  return (
    <FieldLabel label={labelText} readOnly={readOnly}>
      <select
        value={selectValue}
        onChange={handleSelectChange}
        disabled={readOnly}
        aria-label={labelText}
        data-testid={`border-color-select-${name}`}
        style={{ ...dropdownStyle, ...(readOnly && disabledSelectControlStyle) }}
      >
        {ROLE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        <option value={CUSTOM_OPTION_VALUE}>Custom</option>
      </select>

      {value.style === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <input
            type='color'
            value={hexColorSwatchValue(hex, CUSTOM_DEFAULT)}
            onChange={hexInput.handleSwatchChange}
            disabled={readOnly}
            aria-label={`${labelText} custom color swatch`}
            data-testid={`border-color-swatch-${name}`}
            style={{
              width: 32,
              height: 32,
              // Both of these matter *here* specifically, unlike the otherwise-identical swatches in
              // `color-role-field.tsx`/`background-field.tsx`. This is the only custom color widget
              // mounted inside a Puck `object` field (`border.color` on Section/FlexLayout), so Puck
              // indents it into a noticeably narrower container than a top-level field gets. A flex
              // item defaults to `min-width: auto`, and an `<input type=text>` resolves that to its
              // large intrinsic width (~180px at the default `size`), so the hex input below refuses
              // to shrink and pushes the entire overflow onto this swatch -- which, at the default
              // `flex-shrink: 1`, collapses from 32px to a few px sliver. Pinning the swatch and
              // letting the text input shrink instead is what keeps it square.
              flexShrink: 0,
              padding: 0,
              border: '1px solid var(--dcm-cms-border-color-border-color, #d0d4d8)',
              borderRadius: 'var(--dcm-cms-border-color-border-radius, 4px)',
              cursor: 'pointer',
            }}
          />
          <input
            type='text'
            value={hexInput.text}
            onChange={hexInput.handleChange}
            disabled={readOnly}
            aria-label={`${labelText} custom color value`}
            aria-invalid={hexInput.invalid || undefined}
            aria-describedby={hexInput.invalid ? hexInput.errorId : undefined}
            data-testid={`border-color-hex-${name}`}
            style={{
              ...fieldControlStyle,
              flex: 1,
              // Counterpart to the swatch's `flexShrink: 0` above: overrides this input's default
              // `min-width: auto` so it can actually shrink to the container instead of holding its
              // ~180px intrinsic width and squeezing its sibling. Same fix flex-layout.tsx applies
              // to its own slot for the same reason.
              minWidth: 0,
              padding: `var(--dcm-cms-border-color-select-padding, ${FIELD_CONTROL_PADDING})`,
              borderColor:
                'var(--dcm-cms-border-color-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)))',
              borderRadius: `var(--dcm-cms-border-color-border-radius, ${FIELD_CONTROL_RADIUS})`,
              fontSize: `var(--dcm-cms-border-color-font-size, ${FIELD_CONTROL_FONT_SIZE})`,
              fontFamily: 'var(--dcm-cms-border-color-font-family, monospace)',
            }}
          />
        </div>
      )}
      {value.style === 'custom' && hexInput.invalid && <HexColorError id={hexInput.errorId} />}
    </FieldLabel>
  );
}
